import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { I18nProvider } from './i18n';
import './styles/App.css';
import RouteLoadErrorBoundary from './components/common/RouteLoadErrorBoundary';

const loadWorkspaceApp = () =>
  import('./components/workspace/WorkspaceApp').then((module) => {
    module.preloadWorkspaceEditor?.().catch(() => undefined);
    return module;
  });
const loadShareView = () => import('./components/share/ShareView');
const loadCommercialLandingPage = () => import('./components/landing/CommercialLandingPage');

const WorkspaceApp = lazy(loadWorkspaceApp);
const ShareView = lazy(loadShareView);
const CommercialLandingPage = lazy(loadCommercialLandingPage);

const TRANSIENT_URL_QUERY_PARAMS = Object.freeze(['_reload']);
const SHARE_ROUTE_PATTERN = /^\/s\/([^/]+)$/;

const decodeRouteSegment = (segment) => {
  if (typeof segment !== 'string' || segment.length === 0) {
    return null;
  }

  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const normalizePathname = (pathname) => {
  if (typeof pathname !== 'string') {
    return '/';
  }

  const trimmedPathname = pathname.trim();
  if (!trimmedPathname || !trimmedPathname.startsWith('/')) {
    return '/';
  }

  if (trimmedPathname === '/') {
    return '/';
  }

  const normalizedPathname = trimmedPathname.replace(/\/+$/, '');

  return normalizedPathname || '/';
};

const normalizeSearch = (search) => {
  if (typeof search !== 'string' || search.length === 0) {
    return '';
  }

  return search.startsWith('?') ? search : `?${search}`;
};

const normalizeHash = (hash) => {
  if (typeof hash !== 'string' || hash.length === 0) {
    return '';
  }

  return hash.startsWith('#') ? hash : `#${hash}`;
};

const isKnownClientRoutePath = (pathname) => {
  const normalizedPathname = normalizePathname(pathname);
  if (normalizedPathname === '/' || normalizedPathname === '/app' || normalizedPathname === '/pricing') {
    return true;
  }

  return SHARE_ROUTE_PATTERN.test(normalizedPathname);
};

const isEligibleClientNavigationEvent = (event) =>
  Boolean(
    event
      && event.button === 0
      && !event.defaultPrevented
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey
      && !event.shiftKey
  );

const findClosestAnchor = (target) => {
  if (!target) {
    return null;
  }

  if (typeof target.closest === 'function') {
    return target.closest('a[href]');
  }

  if (target.parentElement && typeof target.parentElement.closest === 'function') {
    return target.parentElement.closest('a[href]');
  }

  return null;
};

const shouldIgnoreAnchorNavigation = (anchorElement) => {
  if (!anchorElement) {
    return true;
  }

  const rawHref = (anchorElement.getAttribute('href') || '').trim();
  const normalizedHref = rawHref.toLowerCase();
  if (
    !rawHref
    || rawHref.startsWith('#')
    || normalizedHref.startsWith('mailto:')
    || normalizedHref.startsWith('tel:')
    || normalizedHref.startsWith('javascript:')
    || normalizedHref.startsWith('data:')
  ) {
    return true;
  }

  if (anchorElement.hasAttribute('download')) {
    return true;
  }

  if (anchorElement.getAttribute('aria-disabled') === 'true' || anchorElement.hasAttribute('disabled')) {
    return true;
  }

  if (anchorElement.hasAttribute('data-router-ignore')) {
    return true;
  }

  const target = (anchorElement.getAttribute('target') || '').trim();
  if (target && target.toLowerCase() !== '_self') {
    return true;
  }

  const rel = (anchorElement.getAttribute('rel') || '').toLowerCase();
  if (rel.includes('external')) {
    return true;
  }

  return false;
};

const clearTransientUrlQueryParams = (url) => {
  let didMutate = false;

  TRANSIENT_URL_QUERY_PARAMS.forEach((queryParam) => {
    if (url.searchParams.has(queryParam)) {
      url.searchParams.delete(queryParam);
      didMutate = true;
    }
  });

  return didMutate;
};

let workspacePrefetchPromise = null;

const prefetchWorkspaceApp = () => {
  if (!workspacePrefetchPromise) {
    workspacePrefetchPromise = loadWorkspaceApp().catch((error) => {
      workspacePrefetchPromise = null;
      throw error;
    });
  }

  return workspacePrefetchPromise;
};

const shouldWarmWorkspaceBundle = () => {
  if (typeof navigator === 'undefined') {
    return true;
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) {
    return true;
  }

  if (connection.saveData) {
    return false;
  }

  const effectiveType = connection.effectiveType || '';
  return effectiveType !== 'slow-2g' && effectiveType !== '2g';
};

const getRouteFromPath = (pathnameOverride = null) => {
  if (typeof window === 'undefined' && typeof pathnameOverride !== 'string') {
    return { type: 'workspace' };
  }

  const pathname =
    typeof pathnameOverride === 'string'
      ? pathnameOverride
      : window.location.pathname || '/';
  const normalizedPathname = normalizePathname(pathname);
  const shareMatch = normalizedPathname.match(SHARE_ROUTE_PATTERN);
  if (shareMatch) {
    const shareId = decodeRouteSegment(shareMatch[1]);
    if (shareId) {
      return { type: 'share', shareId };
    }
  }

  if (normalizedPathname === '/app') {
    return { type: 'workspace' };
  }

  if (normalizedPathname === '/pricing') {
    return { type: 'pricing' };
  }

  return { type: 'landing' };
};

const areRoutesEqual = (leftRoute, rightRoute) => {
  if (leftRoute === rightRoute) {
    return true;
  }

  if (!leftRoute || !rightRoute) {
    return false;
  }

  return leftRoute.type === rightRoute.type && leftRoute.shareId === rightRoute.shareId;
};

const RouteFallback = () => (
  <div className="route-loading-screen">
    <div className="route-loading-orbit" aria-hidden="true" />
    <p className="route-loading-text">Loading OceanMind...</p>
  </div>
);

const App = () => {
  const [route, setRoute] = useState(() => getRouteFromPath());
  const routeErrorBoundaryKey =
    route.type === 'share'
      ? `share:${route.shareId || ''}`
      : route.type;

  const syncRouteFromLocation = useCallback(() => {
    const nextRoute = getRouteFromPath();
    setRoute((currentRoute) => (
      areRoutesEqual(currentRoute, nextRoute) ? currentRoute : nextRoute
    ));
  }, []);

  const navigateTo = useCallback((nextPathname, navigationOptions = {}) => {
    if (typeof window === 'undefined' || typeof nextPathname !== 'string') {
      return;
    }

    const normalizedPathname = normalizePathname(nextPathname);
    if (!normalizedPathname.startsWith('/')) {
      return;
    }

    const normalizedSearch = normalizeSearch(navigationOptions.search);
    const normalizedHash = normalizeHash(navigationOptions.hash);
    const useReplace = navigationOptions.replace === true;
    const nextLocation = `${normalizedPathname}${normalizedSearch}${normalizedHash}`;

    const currentLocation = `${normalizePathname(window.location.pathname || '/')}${window.location.search || ''}${window.location.hash || ''}`;

    if (currentLocation !== nextLocation) {
      if (useReplace) {
        window.history.replaceState({}, '', nextLocation);
      } else {
        window.history.pushState({}, '', nextLocation);
      }
    }

    syncRouteFromLocation();
  }, [syncRouteFromLocation]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const hasTransientQueryParams = clearTransientUrlQueryParams(currentUrl);
    if (!hasTransientQueryParams) {
      return;
    }

    navigateTo(currentUrl.pathname, {
      replace: true,
      search: currentUrl.search,
      hash: currentUrl.hash
    });
  }, [navigateTo]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePopState = () => {
      syncRouteFromLocation();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [syncRouteFromLocation]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if ((route.type !== 'landing' && route.type !== 'pricing') || !shouldWarmWorkspaceBundle()) {
      return undefined;
    }

    const warmBundle = () => {
      prefetchWorkspaceApp().catch(() => undefined);
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(warmBundle, { timeout: 2500 });
      return () => {
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleId);
        }
      };
    }

    const timer = window.setTimeout(warmBundle, 1600);
    return () => window.clearTimeout(timer);
  }, [route.type]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const resolveClientRouteUrl = (anchorElement) => {
      if (shouldIgnoreAnchorNavigation(anchorElement)) {
        return null;
      }

      let resolvedUrl;
      try {
        resolvedUrl = new URL(anchorElement.href, window.location.href);
      } catch {
        return null;
      }

      if (resolvedUrl.origin !== window.location.origin) {
        return null;
      }

      return isKnownClientRoutePath(resolvedUrl.pathname) ? resolvedUrl : null;
    };

    const warmWorkspaceBundleFromAnchor = (anchorElement) => {
      const resolvedUrl = resolveClientRouteUrl(anchorElement);
      if (!resolvedUrl || normalizePathname(resolvedUrl.pathname) !== '/app') {
        return;
      }

      if (!shouldWarmWorkspaceBundle()) {
        return;
      }

      prefetchWorkspaceApp().catch(() => undefined);
    };

    const handleDocumentClick = (event) => {
      if (!isEligibleClientNavigationEvent(event)) {
        return;
      }

      const anchorElement = findClosestAnchor(event.target);
      if (!anchorElement) {
        return;
      }

      const resolvedUrl = resolveClientRouteUrl(anchorElement);
      if (!resolvedUrl) {
        return;
      }

      const targetPathname = normalizePathname(resolvedUrl.pathname);
      const currentPathname = normalizePathname(window.location.pathname || '/');

      const targetSearch = resolvedUrl.search || '';
      const targetHash = resolvedUrl.hash || '';
      const currentSearch = window.location.search || '';
      const currentHash = window.location.hash || '';

      const isSamePathname = targetPathname === currentPathname;
      const isSameSearch = targetSearch === currentSearch;
      const isSameHash = targetHash === currentHash;

      if (isSamePathname && isSameSearch && isSameHash) {
        event.preventDefault();
        return;
      }

      if (isSamePathname && isSameSearch && !isSameHash) {
        return;
      }

      event.preventDefault();
      navigateTo(resolvedUrl.pathname, {
        search: targetSearch,
        hash: targetHash
      });
    };

    const handlePointerEnter = (event) => {
      warmWorkspaceBundleFromAnchor(findClosestAnchor(event.target));
    };

    const handleFocusIn = (event) => {
      warmWorkspaceBundleFromAnchor(findClosestAnchor(event.target));
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('pointerenter', handlePointerEnter, true);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('pointerenter', handlePointerEnter, true);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [navigateTo]);

  let content = null;
  if (route.type === 'share') {
    content = (
      <ShareView
        shareId={route.shareId}
        onNavigate={navigateTo}
        onEnterAppIntent={prefetchWorkspaceApp}
      />
    );
  } else if (route.type === 'workspace') {
    content = <WorkspaceApp />;
  } else if (route.type === 'pricing') {
    content = (
      <CommercialLandingPage
        initialSectionId="pricing"
        onEnterAppIntent={prefetchWorkspaceApp}
        onNavigate={navigateTo}
      />
    );
  } else {
    content = (
      <CommercialLandingPage
        onEnterAppIntent={prefetchWorkspaceApp}
        onNavigate={navigateTo}
      />
    );
  }

  return (
    <I18nProvider>
      <RouteLoadErrorBoundary resetKey={routeErrorBoundaryKey}>
        <Suspense fallback={<RouteFallback />}>{content}</Suspense>
      </RouteLoadErrorBoundary>
    </I18nProvider>
  );
};

export default App;
